import forge from 'node-forge';
import { API_ENDPOINTS } from '@/api/apiEndpoints';
import { axiosClient } from '@/api/axiosClient';
import { setStoredToken, setStoredCredentials } from '@/api/interceptors';
import { googleMapsApiSettings } from '@/services/googleMapsApiSettings';
import type { AuthSession, User } from '@/models';

function encryptWithPublicKey(data: string, publicKeyBase64: string): string {
  const pkBytes = forge.util.decode64(publicKeyBase64);
  const asn1 = forge.asn1.fromDer(forge.util.createBuffer(pkBytes));
  const pub = forge.pki.publicKeyFromAsn1(asn1);
  const encrypted = pub.encrypt(data, 'RSA-OAEP');
  return forge.util.encode64(encrypted);
}

function mapUserServiceToCCUser(raw: Record<string, unknown>): User {
  // Most endpoints wrap their payload in { status, message, data }. This one has been seen
  // both ways; reading the envelope as the profile leaves every field undefined, which used
  // to silently mint an 'agent'. Unwrap when the shape says to.
  const envelope = raw as { data?: unknown; status?: unknown };
  const data = (
    envelope.data && typeof envelope.data === 'object' && !Array.isArray(envelope.data)
      ? envelope.data
      : raw
  ) as Record<string, unknown>;

  // Confirmed live: this endpoint sends `role` as an already-computed tier ("agent"), not a
  // role id — there is no role_id/roleId field here at all. A previous version of this function
  // required one and threw "Could not determine your role" on every single login as a result.
  // 'call center admin' is kept as a translation entry in case some response ever sends the raw
  // display name instead of an already-mapped tier; anything else falls through to 'agent'.
  const roleMap: Record<string, string> = {
    admin: 'admin',
    'call center admin': 'admin',
    supervisor: 'supervisor',
    agent: 'agent',
  };
  const roleName = String(data.role || data.role_name || data.roleName || '').trim().toLowerCase();
  // A missing role is a broken profile response, not an agent. Defaulting it silently
  // demoted admins whenever this call came back in an unexpected shape, and the role guards
  // then bounced them to /unauthorized. Fail loudly instead — the caller surfaces the error
  // and the session is not established with the wrong permissions.
  if (!roleName) {
    throw new Error('Could not determine your role from the profile response. Please sign in again.');
  }
  // A role that exists but is not in the map is a real (if unknown) role: fall back to the
  // least-privileged one rather than guessing upward.
  const mappedRole = (roleMap[roleName] || 'agent') as User['role'];

  const titleMap: Record<string, string> = {
    admin: 'Administrator',
    supervisor: 'Supervisor',
    agent: 'Call Center Agent',
  };

  const googleMapsApiKey = String(data.googleMapsApiKey ?? data.googlemapsapikey ?? '').trim();
  const enableGoogleApi = Boolean(data.enableGoogleApi ?? data.enablegoogleapi ?? false);

  const firstName = String(data.firstname || data.firstName || '');
  const lastName = String(data.lastname || data.lastName || '');
  const fullName = firstName || lastName ? `${firstName} ${lastName}`.trim() : String(data.name || '');

  return {
    id: String(data.id || ''),
    agentId: String(data.username || data.userName || data.agentId || ''),
    name: fullName,
    email: String(data.email || ''),
    role: mappedRole,
    title: String(data.speciality || data.title || titleMap[mappedRole] || 'Call Center Agent'),
    district: 'All',
    avatarUrl: undefined,
    enableGoogleApi,
    googleMapsApiKey,
  };
}

export interface LoginCredentials {
  userID: string;
  pin: string;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthSession> {
    // 1. Get RSA public key from User Service
    const { data: keyRes } = await axiosClient.get<{
      status: string;
      data: string;
    }>(API_ENDPOINTS.auth.publicKey);
    const publicKey = keyRes.data;
    if (!publicKey) {
      throw new Error('Failed to retrieve public key');
    }

    // 2. Encrypt credentials payload
    const payload = JSON.stringify({
      UserId: credentials.userID,
      PassKey: credentials.pin,
    });
    const encryptedPayload = encryptWithPublicKey(payload, publicKey);

    // 3. Send encrypted login request
    const response = await axiosClient.post(
      API_ENDPOINTS.auth.login,
      { payload: encryptedPayload },
    );

    // 4. Extract response data (axios auto-parses JSON)
    const parsed = response.data as any;

    if (parsed.status !== 100 || !parsed.data?.data) {
      throw new Error(parsed.message || 'Login failed');
    }

    const accessToken: string = parsed.data.accessToken;

    setStoredToken(accessToken);
    setStoredCredentials(credentials);

    // Fetch the Call Center profile so enableGoogleApi/googleMapsApiKey come from app_configuration.
    const user = await this.me();

    const session: AuthSession = {
      user,
      token: accessToken,
      expiresAt: '',
    };

    return session;
  },

  async logout(): Promise<void> {
    setStoredToken(null);
    setStoredCredentials(null);
    googleMapsApiSettings.setApiKey('');
    googleMapsApiSettings.setUseGoogleMaps(false);
  },

  async me(): Promise<User> {
    const { data } = await axiosClient.get<Record<string, unknown>>(API_ENDPOINTS.auth.me);
    const user = mapUserServiceToCCUser(data);
    googleMapsApiSettings.setApiKey(user.googleMapsApiKey ?? '');
    googleMapsApiSettings.setUseGoogleMaps(user.enableGoogleApi ?? false);
    return user;
  },
};
